/**
 * Client glue for the unified record search (/api/v1/search) plus the
 * "recent destinations" the palette shows on an empty query.
 */
import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";

/** One server-side record hit (mirrors apps.core.views.GlobalSearchView). */
export interface SearchHit {
  type: string;
  label: string;
  id: string;
  title: string;
  subtitle: string;
  route: string;
  icon: string;
}

interface SearchGroup {
  type: string;
  label: string;
  count: number;
}

interface SearchResponse {
  query: string;
  results: SearchHit[];
  groups: SearchGroup[];
}

const MIN_LEN = 2;

/** Debounce a fast-changing value (keystrokes) by ``delay`` ms. */
export function useDebouncedValue<T>(value: T, delay = 200): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * Fan-out record search. Only fires for queries of >= 2 chars; keeps the
 * previous page of results visible while the next query is in flight so the
 * list doesn't flicker empty between keystrokes.
 */
export function useRecordSearch(query: string) {
  const enabled = query.trim().length >= MIN_LEN;
  return useQuery({
    queryKey: ["global-search", query.trim()],
    queryFn: () =>
      api.get<SearchResponse>(
        `/search/?q=${encodeURIComponent(query.trim())}`,
      ),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

// --- Recent destinations -----------------------------------------------------

const RECENT_KEY = "hwb.search.recent";
const RECENT_MAX = 6;

export interface RecentEntry {
  /** Command id (static) or ``hit:<type>:<id>`` for a record. */
  key: string;
  title: string;
  subtitle?: string;
  route: string;
  icon: string;
}

export function getRecent(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RecentEntry[]) : [];
  } catch {
    return [];
  }
}

export function pushRecent(entry: RecentEntry): void {
  try {
    const existing = getRecent().filter((e) => e.key !== entry.key);
    const next = [entry, ...existing].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable (private mode / quota) — recents are a
    // nicety, not load-bearing. Fail silently.
  }
}
